import {
  Box,
  Checkbox,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { usePushSkuNinjaMutation } from 'common/hooks/api/mutations/use-content-mutation';
import { getPDPValues } from 'common/services/attributes.service';
import { Styles } from 'common/types/styles';
import { ButtonWithLoading } from 'common/ui/containers/button-with-loading';
import { Modal } from 'common/ui/containers/modal';
import { downloadJSON } from 'common/utils/json';
import { useSelectedProductQuery } from 'modules/common/product-details-page/hooks/queries';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';

const styles: Styles = {
  tableContainer: { height: '500px' },
  table: { tableLayout: 'fixed' },
  cell: { width: 150, wordBreak: 'break-all' },
  pushButton: { mt: 2 },
  toggleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '5',
    justifyContent: 'flex-end',
  },
  toggle: { fontSize: 14, whiteSpace: 'nowrap' },
  pushButtonWrappers: { display: 'flex', flexDirection: 'row', gap: 2 },
  result: {
    mt: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: 2,
    boxSizing: 'border-box',
    backgroundColor: '#f5f5f5',
  },
};

interface Props {
  pid: string;
  open: boolean;
  onClose: () => void;
}

export function PdpSkuNinjaModal({ pid, open, onClose }: Props): ReactElement {
  const [selected, setSelected] = useState<string[]>([]);
  const [result, setResult] = useState<Array<{ key: string; value: unknown }>>([]);
  const [isChangedOnly, setIsChangedOnly] = useState<boolean>(false);

  const { data: productData, isLoading } = useSelectedProductQuery();

  const pdp = useMemo(() => getPDPValues(productData), [productData]);

  const { mutate: mutateSkuNinja } = usePushSkuNinjaMutation();

  const { title } = pdp.initial;
  const description = pdp.initial.desc;
  const features = pdp.initial.feats;

  const extractArray = (value: string | unknown[]) => {
    return Array.isArray(value) ? value.join('\n') : value;
  };

  const previousTitle = useMemo(() => {
    return extractArray(pdp.ai.title);
  }, [pdp.ai.title]);

  const previousDesc = useMemo(() => {
    return extractArray(pdp.ai.desc);
  }, [pdp.ai.desc]);

  const previousFeatures = useMemo(() => {
    return extractArray(pdp.ai.feats);
  }, [pdp.ai.feats]);

  const pdpData = useMemo(() => {
    return [
      {
        key: 'product_name',
        title: 'Product Title',
        initial: title ?? '',
        final: previousTitle ?? '',
      },
      {
        key: 'product_short_description',
        title: 'Short Description',
        initial: description ?? '',
        final: previousDesc ?? '',
      },
      {
        key: 'product_long_description',
        title: 'Long Description',
        initial: features ?? '',
        final: previousFeatures ?? '',
      },
    ]?.filter(item => (isChangedOnly ? item.final !== item.initial && item.final?.[0] !== item.initial : Boolean));
  }, [description, features, isChangedOnly, previousDesc, previousFeatures, previousTitle, title]);

  const formatFinalValue = (value: string | string[]) => {
    if (Array.isArray(value)) return value.join(', ');
    return value;
  };

  const handleSwitchChangedOnly = () => {
    setSelected([]);
    setIsChangedOnly(!isChangedOnly);
  };

  const handleSkuNinjaPush = (download: boolean) => {
    const selectedAttributes = pdpData
      ?.filter(item => selected?.includes(item.key))
      .reduce<Record<string, string>>((acc, item) => {
        acc[item?.key] = formatFinalValue(item?.final);
        return acc;
      }, {});

    mutateSkuNinja(
      {
        body: {
          attributes: selectedAttributes,
        },
        params: {
          pid,
          download,
        },
      },
      {
        onSuccess: data => {
          if (download) {
            downloadJSON(data, 'skuninja_data.json');
          } else {
            setResult(
              data?.result
                ? Object.entries(data?.result || {}).map(([key, value]) => ({
                    key,
                    value,
                  }))
                : []
            );
          }
        },
      }
    );
  };

  const handleSelect = (key: string) => {
    if (selected.includes(key)) {
      setSelected(selected.filter(item => item !== key));
    } else {
      setSelected([...selected, key]);
    }
  };

  const handleSelectAll = () => {
    if (selected?.length === pdpData?.length) {
      setSelected([]);
    } else {
      setSelected(pdpData?.map(item => item.key) || []);
    }
  };

  const getAttrResultValue = (value: unknown) => {
    switch (typeof value) {
      case 'boolean': {
        return value ? 'Yes' : 'No';
      }
      case 'object': {
        return JSON.stringify(value);
      }
      default:
        return String(value);
    }
  };

  useEffect(() => {
    if (open) return;

    setSelected([]);
    setResult([]);
    setIsChangedOnly(false);
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} maxWidth="md" title="Push to SKU Ninja">
      <Box sx={styles.toggleWrapper}>
        <Typography sx={styles.toggle}>Changed Only</Typography>
        <Switch checked={isChangedOnly} onClick={handleSwitchChangedOnly} color="primary" />
      </Box>
      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox checked={selected?.length === pdpData?.length} onChange={handleSelectAll} />
              </TableCell>
              <TableCell sx={styles.cell}>Name</TableCell>
              <TableCell sx={styles.cell}>Original</TableCell>
              <TableCell align="center" sx={styles.cell} />
              <TableCell sx={{ ...styles.cell, color: 'green', fontWeight: 500 }}>Final</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pdpData?.map(item => (
              <TableRow key={item.key}>
                <TableCell>
                  <Checkbox checked={selected?.includes(item.key)} onChange={() => handleSelect(item.key)} />
                </TableCell>
                <TableCell sx={styles.cell}>{item.title}</TableCell>
                <TableCell sx={styles.cell}>{item.initial !== undefined ? item.initial : '-'}</TableCell>
                <TableCell align="center" sx={styles.cell}>
                  {'->'}
                </TableCell>
                <TableCell sx={{ ...styles.cell, color: 'green', fontWeight: 500 }}>
                  {item.final?.length ? formatFinalValue(item.final) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={styles.pushButtonWrappers}>
        <ButtonWithLoading
          sx={styles.pushButton}
          fullWidth
          onClick={() => handleSkuNinjaPush(true)}
          disabled={!selected?.length}
          loading={isLoading}
        >
          <DownloadIcon />
          Download
        </ButtonWithLoading>
        <ButtonWithLoading
          sx={styles.pushButton}
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => handleSkuNinjaPush(false)}
          disabled={!selected?.length}
          loading={isLoading}
        >
          Push
        </ButtonWithLoading>
      </Box>

      {Boolean(result?.length) && (
        <Box sx={styles.result}>
          <Typography variant="h6">Result:</Typography>
          {result?.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result?.map(row => (
                  <TableRow key={row?.key}>
                    <TableCell>{row?.key ?? ''}</TableCell>
                    <TableCell>{row?.value ? getAttrResultValue(row?.value) : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography>No data available</Typography>
          )}
        </Box>
      )}
    </Modal>
  );
}
